import React, { useState, useEffect } from 'react';
import supabase from '../../supabase';
import FullPageLoader from '../../components/FullPageLoader';
import Sidebanner from './sidebanner';
import { useAuth } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';

export default function Intel() {
  const { business, loading: authLoading } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new'); // new | resolved | ignored

 useEffect(() => {
    // 3. The Guard: Don't fetch intel if we don't know who the business is yet
    if (authLoading || !business?.www) return;

    async function fetchIntelData() {
      setLoading(true);
      try {
        // 4. Fetch ONLY the page-specific intel data
        const { data: intelData, error } = await supabase
          .from('business_intel')
          .select('*')
          .eq('business_url', business.www) 
          .eq('status', filter)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setReports(intelData || []);
      } catch (error) {
        console.error('Forensic Fetch Error:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchIntelData();
    // 5. Re-run if filter changes OR if the business data finally arrives
  }, [filter, business?.www, authLoading]); // Re-run when filter changes

  const updateStatus = async (id, newStatus) => {
    const { error } = await supabase
      .from('business_intel')
      .update({ status: newStatus })
      .eq('id', id);

    if (!error) {
      // Optimistic UI update: remove from current view
      setReports(prev => prev.filter(r => r.id !== id));
    }
  };

  if (authLoading) return <FullPageLoader />;

  return (
    <div className='min-h-screen bg-[#f9fafb] pt-[90px] lg:pt-[120px] w-full flex flex-col px-[22px] lg:pr-[250px] xl:pr-[350px] font-poppins pb-20'>
             <Helmet>
              <title>Private Intel | Sitemanifest</title> 
              
              <meta name="robots" content="noindex, nofollow" />
              
              <meta name="googlebot" content="noarchive" />
            </Helmet>
      
      {/* Header (Matching Homepage Style) */}
      <div className='max-w-[750px] mx-auto w-full mb-8 text-center md:text-left'>
        <h1 className='text-3xl font-black text-[#1a1a1a]'>Intel Reports</h1>
        <p className='text-gray-500 text-sm'>
          Private feedback for <span className='text-blue-600 font-bold'>{business?.title}</span>
        </p>
      </div>

      <div className='max-w-[750px] mx-auto w-full'>
        {/* Filter Tabs (Professional & Minimal) */}
        <div className='flex gap-8 border-b border-gray-200 mb-8 px-2'>
          {['new', 'resolved', 'ignored'].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`pb-4 text-[11px] font-black uppercase tracking-widest relative transition-all ${
                filter === tab ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab}
              {filter === tab && <div className='absolute bottom-0 left-0 w-full h-[3px] bg-blue-600 rounded-t-full' />}
            </button>
          ))}
        </div>

        {/* Reports Feed */}
        <div className='flex flex-col gap-6'>
          {reports.length === 0 ? (
            <div className='bg-white border border-gray-100 rounded-[32px] p-20 text-center shadow-sm'>
              <p className='text-gray-400 text-xs font-bold uppercase tracking-widest italic'>
                No {filter} intelligence found
              </p>
            </div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className='bg-white border border-gray-100 rounded-[32px] p-6 lg:p-8 shadow-sm transition-all hover:shadow-md'>
                <div className='flex justify-between items-start mb-6'>
                  <div className='flex items-center gap-4'>
                    <div className='w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center text-white text-[10px] font-black tracking-tighter'>
                      {report.is_anonymous ? 'ANON' : 'USER'}
                    </div>
                    <div>
                      <h4 className='font-black text-[#1a1a1a] text-sm uppercase'>{report.author || 'Anonymous Member'}</h4>
                      <p className='text-[10px] text-gray-400 font-bold uppercase tracking-widest'>
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className='flex gap-2'>
                    {filter === 'new' && (
                      <>
                        <button 
                          onClick={() => updateStatus(report.id, 'resolved')}
                          className='px-4 py-2 bg-green-50 text-green-600 text-[10px] font-black rounded-xl hover:bg-green-600 hover:text-white transition-all uppercase'
                        >
                          Resolve
                        </button>
                        <button 
                          onClick={() => updateStatus(report.id, 'ignored')}
                          className='px-4 py-2 bg-gray-50 text-gray-400 text-[10px] font-black rounded-xl hover:bg-red-50 hover:text-red-500 transition-all uppercase'
                        >
                          Ignore
                        </button>
                      </>
                    )}
                    {filter !== 'new' && (
                       <button 
                       onClick={() => updateStatus(report.id, 'new')}
                       className='px-4 py-2 bg-blue-50 text-blue-600 text-[10px] font-black rounded-xl hover:bg-blue-600 hover:text-white transition-all uppercase'
                     >
                       Re-open
                     </button>
                    )}
                  </div>
                </div>

                <div className='bg-gray-50/50 p-6 rounded-2xl border border-gray-100 relative'>
                   <div className='absolute -top-2 left-6 px-2 bg-white text-[8px] font-black text-blue-600 uppercase tracking-[2px] border border-blue-100 rounded-full'>
                     Secret Intel
                   </div>
                   <p className='text-gray-700 font-medium text-sm leading-relaxed italic'>
                     "{report.content}"
                   </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Sidebanner />
    </div>
  );
}